import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Keyboard shortcut overlay (v0.4.0 polish #34).
 *
 * Press `?` anywhere outside a text field to open. Press Esc to close.
 * Mounted globally inside the AppShellLayout so it works on every authed
 * page. Currently lists the global shortcuts plus a slot for the active
 * route to declare its own (passed via children).
 */
type Shortcut = { keys: string; description: string };

const GLOBAL_SHORTCUTS: Shortcut[] = [
  { keys: "?", description: "Show this keyboard shortcuts overlay" },
  { keys: "Esc", description: "Close menu / modal / overlay" },
  { keys: "g h", description: "Go to Home" },
  { keys: "g s", description: "Go to My schedule" },
  { keys: "g p", description: "Go to Preferences" },
];

export function KbdShortcuts({ extras }: { extras?: Shortcut[] }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      const inField =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (e.target as HTMLElement | null)?.isContentEditable;
      if (inField) return;
      if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const all: { group: string; items: Shortcut[] }[] = [
    { group: "Global", items: GLOBAL_SHORTCUTS },
    ...(extras && extras.length > 0
      ? [{ group: "This page", items: extras }]
      : []),
  ];

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="kbd-title"
      className="sheet-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="sheet-panel max-w-md">
        <div className="flex items-center justify-between">
          <h2 id="kbd-title" className="text-base font-semibold text-fg-0">
            Keyboard shortcuts
          </h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="grid h-8 w-8 place-items-center rounded-md text-fg-2 transition-colors hover:bg-ink-2 hover:text-fg-0"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="mt-4 space-y-4">
          {all.map((section) => (
            <div key={section.group}>
              <p className="kicker">{section.group}</p>
              <dl className="mt-2 space-y-1.5">
                {section.items.map((s) => (
                  <div key={s.keys} className="flex items-center justify-between gap-3">
                    <dt className="text-sm text-fg-1">{s.description}</dt>
                    <dd>
                      <kbd className="font-mono text-[11px] px-1.5 py-0.5 rounded border border-ink-3 bg-ink-2 text-fg-0">
                        {s.keys}
                      </kbd>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
        <p className="mt-5 text-xs text-fg-3">
          Press <kbd className="font-mono">?</kbd> or <kbd className="font-mono">Esc</kbd> to close.
        </p>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Polish v0.4.0 #35: global g+letter navigation. Hooked in AppShellLayout
 * so any page reachable from primary nav is one chord away.
 */
export function useGlobalGotoShortcuts() {
  useEffect(() => {
    let lastG = 0;
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const now = Date.now();
      if (e.key === "g") {
        lastG = now;
        return;
      }
      if (now - lastG > 1200) return;
      const map: Record<string, string> = {
        h: "/",
        s: "/my-schedule",
        p: "/preferences",
        t: "/time-off",
        o: "/open-shifts",
        n: "/notifications",
      };
      const path = map[e.key];
      if (path) {
        e.preventDefault();
        window.location.hash = "";
        window.history.pushState({}, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
      lastG = 0;
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
